import * as React from 'react'
import Select, { components } from 'react-select';
import * as classNames from 'classnames';
import { useFieldState, useFieldApi, useField, FormError } from 'informed';
import { replaceDotsWithHyphens } from '../../misc/Helpers/StringHelper';

export interface MultiSelectProps {
    id: string;
    disabled?: boolean;
    field: string;
    className?: string;
    options: any[];
    labelKey: string;
    valueKey: string;
    onChange?: () => void;
    onBlur?: (e) => void;
    validate?: (value: any, values: any[]) => FormError;
    multi?: boolean;
    allowSelectAll?: boolean;
    menuPlacement?: 'top' | 'bottom' | 'auto',
    required?: boolean
}

const MultiSelectContainer = (containerProps) => {
  const { selectProps } = containerProps;
  const { ["data-automationid"]: automationId } = selectProps;

  return (
    <components.SelectContainer
      {...containerProps}
      innerProps={{
        ...containerProps.innerProps,
        ...(automationId ? { "data-automationid": automationId } : {}),
      }}
    />
  );
};

export const MultiSelect = ({ field, className, options, labelKey, valueKey, onChange, onBlur, multi, allowSelectAll, validate, ...props }: MultiSelectProps) => {
    const { fieldState, fieldApi } = useField({ field: field, validate: validate });
    const { setValue, setTouched, getError } = fieldApi;

    const vitalographStyles = {
        control: (provided) => ({
            ...provided,
            borderColor: '#e5e5e5',
        }),
        multiValueRemove: (provided) => ({
            ...provided,
            color: '#555555'
        }),
        dropdownIndicator: (provided) => ({
            ...provided,
            color: '#555555'
        }),
        menu: (provided) => ({
            ...provided,
            zIndex: 10,
        })
    }

    const allOption = {
        [labelKey]: "Select All",
        [valueKey]: "*"
    };

    return (<>
        <Select
            components={{ SelectContainer: MultiSelectContainer }}
            id={props.id}
            data-automationid={replaceDotsWithHyphens(field)}
            menuPlacement={props.menuPlacement}
            name={field}
            isDisabled={props.disabled}
            styles={vitalographStyles}
            options={allowSelectAll ? [allOption, ...options] : options}
            value={fieldState.value ? fieldState.value : null}
            classNamePrefix="multiSelect"
            getOptionLabel={o => o[labelKey]}
            getOptionValue={o => o[valueKey]}
            onChange={(e) => {
                if (allowSelectAll && e && e.includes(allOption)) {
                    setValue(options);
                }
                //A multiselect with multi=true passes an array when it changes, but it can be empty.
                //An empty array is considered a null value
                else if (e && Array.isArray(e)) {
                    e.length > 0 ? setValue(e) : setValue(null);
                } else {
                    setValue(e);
                }

                if (onChange)
                    onChange();
            }}
            onBlur={e => {
                setTouched(true);
                if (onBlur) {
                    onBlur(e);
                }
            }}
            isMulti={multi}
            {...props}
        />
        {getError() ? (
            <span className="field-validation-error">{getError()}</span>
        ) : null}
    </>);
}

export const PrimitiveMultiSelect = ({ field, className, options, labelKey, valueKey, onChange, onBlur, multi, allowSelectAll, validate, required, ...props }: MultiSelectProps) => {
    const { fieldState, fieldApi } = useField({ field: field, validate: validate });
    const { setValue, setTouched, getError } = fieldApi;

    const vitalographStyles = {
        control: (provided) => ({
            ...provided,
            borderColor: fieldState.error ? '#ca0511' : '#e5e5e5',
        }),
        multiValueRemove: (provided) => ({
            ...provided,
            color: '#555555'
        }),
        dropdownIndicator: (provided) => ({
            ...provided,
            color: '#555555'
        }),
        menu: (provided) => ({
            ...provided,
            zIndex: 10,
        })
    }

    const allOption = {
        [labelKey]: "Select All",
        [valueKey]: "*"
    };

    const divClasses = classNames({
        "field-block-error": fieldApi.getError(),
        "field-block-valid": !fieldApi.getError(),
    })

    return (<div className={divClasses}>
        <Select
            components={{ SelectContainer: MultiSelectContainer }}
            menuPlacement={props.menuPlacement}
            name={field}
            data-automationid={replaceDotsWithHyphens(field)}
            isDisabled={props.disabled}
            styles={vitalographStyles}
            options={allowSelectAll ? [allOption, ...options] : options}
            value={fieldState.value ? (Array.isArray(fieldState.value) ? options.filter(e => fieldState.value.some(x => e[valueKey] == x)) : options.find(e => e[valueKey] == fieldState.value)) : null}
            classNamePrefix="multiSelect"
            getOptionLabel={o => o[labelKey]}
            getOptionValue={o => o[valueKey]}
            onChange={(e) => {
                if (allowSelectAll && e && e.includes(allOption)) {
                    setValue(options.map(e => e[valueKey]));
                }
                //A multiselect with multi=true passes an array when it changes, but it can be empty.
                //An empty array is considered a null value
                else if (e && Array.isArray(e)) {
                    e.length > 0 ? setValue(e.map(e => e[valueKey])) : setValue([]);
                } else {
                    setValue(e[valueKey]);
                }

                if (onChange)
                    onChange();
            }}
            onBlur={e => {
                setTouched(true);
                if (onBlur) {
                    onBlur(e);
                }
            }}
            isMulti={multi}
            required={required}
            {...props}
        />
        {getError() ? (
            <span className="field-validation-error">{getError()}</span>
        ) : null}
    </div>);
}
